import React, { useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ButtonText } from 'components';
import SiteSelector from 'components/SiteSelector/SiteSelector';
import { SiteSelectorAppearance } from 'components/SiteSelector/types';
import { theme, toRem } from 'design';
import { EmployerDetails } from 'modals';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Employers, getUserDashboardEmployersRequest } from 'store/Employers';
import { IStore } from 'store/types';
import { EmployeeType, IEmployer, IEmployerSite } from 'types';
import { sortAlphaNumerically } from 'utils/arrays';

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  width: 100%;
  gap: ${toRem(theme.space.sm)};
  margin-bottom: ${toRem(theme.space.sm)};

  button {
    color: ${theme.color.primary[500]};
    text-decoration: none;

    &:hover {
      color: ${theme.color.primary[600]};
    }
  }

  svg {
    position: relative;
    top: ${toRem(2)};
    height: ${toRem(16)} !important;
    width: ${toRem(16)} !important;
  }
`;

const SelectorWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  width: 100%;
`;

const LoadingContainer = styled.div`
  display: block;
  width: 100%;
`;

// Type guard function to check if employer is IEmployer (AKA are they an admin at the organization)
const isAdminAtOrganization = (employer: IEmployer | IEmployerSite): employer is IEmployer => 'type' in employer && employer.type === EmployeeType.ADMIN;

interface IEmployerSiteSelectorProps {
  onChange: (site: IEmployerSite | undefined) => void;
  selectedSite?: string;
  dataCy?: string;
}

const EmployerSiteSelector = ({ onChange, selectedSite, dataCy = 'employer-site-select' }: IEmployerSiteSelectorProps) => {
  const dispatch = useDispatch();
  const isEmployersLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employers.GET_USER_DASHBOARD_EMPLOYERS_REQUEST));
  const [employers, setEmployers] = useState<Array<IEmployerSite | IEmployer>>([]);

  // Fetch all employers and sites
  useEffect(() => {
    dispatch(getUserDashboardEmployersRequest(setEmployers));
  }, []);

  // Combine all employers and sites into a single list
  const employersList: IEmployerSite[] = useMemo(
    () =>
      employers
        .reduce((acc, employer) => {
          if (isAdminAtOrganization(employer)) return [...acc, ...employer.sites];
          return [...acc, employer];
        }, [])
        .sort(sortAlphaNumerically('name')),
    [employers]
  );

  // If a site is selected, find the employer that contains the site
  const currentEmployer = useMemo(() => employersList?.find((emp) => emp.id === selectedSite), [employersList, selectedSite]);

  // Show the employer details modal if an employer is selected
  const [showEmployerDetailsModal, hideEmployerDetailsModal] = useModal(() => <EmployerDetails employer={currentEmployer} onClose={hideEmployerDetailsModal} />, [currentEmployer]);

  return (
    <Layout data-cy={dataCy}>
      {isEmployersLoading ? (
        <LoadingContainer data-cy={`${dataCy}-loading`}>
          <Skeleton width="100%" height={40} />
        </LoadingContainer>
      ) : (
        <>
          <SelectorWrapper data-cy={`${dataCy}-site-selector-container`}>
            <SiteSelector allSitesEnabled allSitesLabel="All Employers" searchPlaceholder="Search Employers" options={employersList} currentSite={currentEmployer} onChange={onChange} appearance={SiteSelectorAppearance.SECONDARY_HEADER} />
          </SelectorWrapper>
          {currentEmployer && <ButtonText label="View Employer Details" fontSize={11} fontWeight="medium" onClick={showEmployerDetailsModal} data-cy={`${dataCy}-view-employer-details-button`} />}
        </>
      )}
    </Layout>
  );
};
export default EmployerSiteSelector;
